import type { MigrateFormat } from '../services/migrateApiService'

export const IMPORT_FORMATS: { value: MigrateFormat; label: string }[] = [
  { value: 'docmorphia', label: 'DocMorphia (no conversion)' },
  { value: 'carbone', label: 'Carbone' },
  { value: 'docmosis', label: 'Docmosis' },
  { value: 'handlebars', label: 'Handlebars' },
]

export function formatLabel(format: MigrateFormat): string {
  return IMPORT_FORMATS.find(f => f.value === format)?.label ?? format
}

/** Strips the .docx extension to seed the editable template name. */
export function defaultTemplateName(fileName: string): string {
  return fileName.replace(/\.docx$/i, '').trim() || fileName
}

/** Prefilled change note, so an import does not stall on a team's RequireChangeNote setting. */
export function defaultChangeNote(format: MigrateFormat): string {
  return format === 'docmorphia' ? 'Initial upload' : `Imported from ${formatLabel(format)}`
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Runs `worker` over every item with at most `limit` in flight, preserving result order. Keeps a
 * multi-file import from opening one request per file at once.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++
      results[i] = await worker(items[i], i)
    }
  })
  await Promise.all(runners)
  return results
}
